import { tWithKeyAttr as t } from "../../i18nHelper";
import React from "react";
import ListPagination from "../listPagination/listPagination";
import TreatmentDocument from "../treatmentDocument/treatmentDocument";
import Loader from "../loader/loader";
import styles from "./pendingTreatmentPlans.module.css";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { useGetAppointmentsProgramsByCompanyIdQuery } from "../../services/api/utils.api";
import { TAppointmentsProgramsData } from "../../services/types/utils.types";
import eyeIcon from "../../images/eye_icon.svg";
import groupUsersIcon from "../../images/group_users_icon_black.svg";
import ExportTableButtons from '../exportTableButtons/ExportTableButtons';

type TabState = "pending" | "pendingTherapist" | "approved";

const ITEMS_PER_PAGE = 9;

const TAB_CONFIG = {
  pending: { label: 'WAITING', dataKey: 'pendingForApproval' },
  pendingTherapist: { label: 'WAITING_FOR_THERAPIST', dataKey: 'pendingForTherapist' },
  approved: { label: 'MAUSHAROT', dataKey: 'assigned' }
} as const;

function PendingTreatmentPlans({ readOnly = false }: { readOnly?: boolean }) {
  const [selectedDocument, setSelectedDocument] = React.useState<TAppointmentsProgramsData | null>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<TabState>("pending");
  const [pagination, setPagination] = React.useState({ start: 1, end: ITEMS_PER_PAGE });

  const admin = useAppSelector(adminSelector);
  const { data, isLoading, error } = useGetAppointmentsProgramsByCompanyIdQuery(
    admin?.companyId,
    { skip: !admin?.companyId }
  );

  const filteredData = React.useMemo(() => {
    if (!data) return [];
    const dataKey = TAB_CONFIG[activeTab].dataKey;
    return data[dataKey] || [];
  }, [data, activeTab]);

  const paginatedData = React.useMemo(() => {
    return filteredData.slice(pagination.start - 1, pagination.end);
  }, [filteredData, pagination]);

  // Update selectedDocument when data refetches
  React.useEffect(() => {
    if (selectedDocument && data) {
      const allData = [
        ...(data.pendingForApproval || []),
        ...(data.pendingForTherapist || []),
        ...(data.assigned || [])
      ];
      
      const updatedDocument = allData.find(
        item => item.appointmentsProgram?.id === selectedDocument.appointmentsProgram?.id
      );
      
      if (updatedDocument) {
        setSelectedDocument(updatedDocument);
      } else {
        setSelectedDocument(null);
      }
    }
  }, [data, selectedDocument?.appointmentsProgram?.id]);

    const handleViewDocument = (item: TAppointmentsProgramsData) => {
    setSelectedDocument(item);
  };

  const handleCloseDocument = () => {
    setSelectedDocument(null);
  };

  const handleRowClick = (id: number | undefined) => {
    setSelectedRowId(id || null);
  };

  const isRowSelected = (item: TAppointmentsProgramsData) => {
    const itemId = item.appointmentsProgram?.id;
    return itemId === selectedDocument?.appointmentsProgram?.id || itemId === selectedRowId;
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return t('DATE_NOT_AVAILABLE');
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString("he-IL");
  };

  if (isLoading) return <section className={`section ${styles.section}`}><Loader /></section>;
  if (error) return <section className={`section ${styles.section}`}><div /></section>;

  return (
    <section className={`section ${styles.section}`}>
      {selectedDocument && (
        <TreatmentDocument
          treatmentPlan={selectedDocument}
          openDocument={true}
          onClose={handleCloseDocument}
          readOnly={readOnly}
        />
      )}

      <article className="section">
        <div className={styles.headerWithToggles}>
          <h4 className={styles.home__sectionTitle}>{t('TREATMENT_PLANS')}</h4>
          
          <div className={styles.sideTogglesContainer}>
            {(Object.keys(TAB_CONFIG) as TabState[]).map((tab) => (
              <button
                key={tab}
                className={`${styles.sideToggle} ${activeTab === tab ? styles.activeSideToggle : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                <span className={styles.sideToggleText}>
                  {t(TAB_CONFIG[tab].label)}
                </span>
                <span className={styles.sideToggleCount}>
                  {data?.[TAB_CONFIG[tab].dataKey]?.length || 0}
                </span>
              </button>
            ))}
          </div>

          <ExportTableButtons
            entity="TreatmentPlans"
            dataRows={filteredData.map(item => {
              const isGroup = !!item.appointmentsProgram?.treatmentGroupId;
              return {
                Id: item.appointmentsProgram?.id ?? '-',
                OpenDate: item.appointmentsProgram?.createDate
                  ? new Date(item.appointmentsProgram.createDate).toLocaleDateString('he-IL')
                  : '-',
                ClientName: isGroup
                  ? item.treatmentGroup?.groupName ?? '-'
                  : item.user?.fullName ?? '-',
                PlanName: item.appointmentsProgram?.name ?? '-',
                TreatmentMethod: item.mediaName ?? '-',
                Tab: t(TAB_CONFIG[activeTab].label),
              };
            })}
          />

          <ul className="list">
            {filteredData.length > 0 ? (
              // header row
              <li className={`listRow ${styles.listRow}`}>
                <p className="listCell">
                  <span className={styles.hideOnMobile}>{t('TREATMENT_PLAN_NUMBER')}</span>
                  <span className={styles.showOnMobile}>{t('PROGRAM_NUMBER')}</span>
                </p>
                <p className="listCell">{t('OPENING_DATE_1')}</p>
                <p className="listCell">
                  <span className={styles.hideOnMobile}>{t('SHEM_MASH')}</span>
                  <span className={styles.showOnMobile}>{t('NAME_2')}</span>
                </p>
                <p className="listCell">{t('PROGRAM_NAME')}</p>
                <p className="listCell">
                  <span className={styles.hideOnMobile}>{t('TREATMENT_METHOD')}</span>
                  <span className={styles.showOnMobile}>{t('METHOD')}</span>
                </p>
              </li>
            ) : (
              <p className={styles.text}>{t('NO_DATA_TO_DISPLAY')}</p>
            )}

            {paginatedData.map((item, idx) => {
              const isGroupTreatment = !!item.appointmentsProgram?.treatmentGroupId;
              const displayName = isGroupTreatment 
                ? item.treatmentGroup?.groupName || "" 
                : item.user?.fullName || "";
              
              return (
                <li
                  key={item.appointmentsProgram?.id || idx}
                  className={`listRow ${styles.listRow} ${
                    idx % 2 === 0 ? "listRow_colored" : ""
                  } ${isRowSelected(item) ? "listRow_selected" : ""}`}
                  onClick={() => handleRowClick(item.appointmentsProgram?.id)}
                >
                  <p className="listCell">{item.appointmentsProgram?.id}</p>
                  <p className="listCell">{formatDate(item.appointmentsProgram?.createDate)}</p>
                  <p className="listCell">
                    {isGroupTreatment && (
                      <img 
                        src={groupUsersIcon} 
                        alt="Group" 
                        className={styles.group_icon}
                        style={{ width: '16px', height: '16px', marginLeft: '8px', verticalAlign: 'middle' }}
                      />
                    )}
                    {displayName}
                  </p>
                  <p className="listCell">{item.appointmentsProgram?.name || ""}</p>
                  <p className="listCell">{item.mediaName || ""}</p>
                  <button
                    className={`listCell ${styles.view_btn} ${styles.btn_take}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(item);
                    }}
                  >
                    <img src={eyeIcon} alt="View Details icon" className={styles.view_icon} />
                    {t('VIEW_DETAILS')}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {filteredData.length > 0 && (
          <div className={styles.paginationContainer}>
            <ListPagination
              limits={pagination}
              total={filteredData.length}
              setLimits={setPagination}
              step={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </article>
    </section>
  );
}

export default PendingTreatmentPlans;